import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Search, ChevronRight } from 'lucide-react';
import { cn } from '@ideoz/ui';
import type { DesignSystem, Platform } from './DesignSystemPanel';
import { borderRadiusToPx, MOBILE_CONVENTIONS } from '../designSystemConstants';
import { DEFAULT_MOBILE_OS } from '../designSystemPayload';
import { loadGoogleFontPreview } from '../googleFonts';

export type DesignSystemLivePreviewProps = {
  designSystem: DesignSystem;
  logoUrl?: string | null;
  className?: string;
};

const SAMPLE_LIST_ITEMS = [
  { titleKey: 'one', statusKey: 'active', tone: 'success' as const },
  { titleKey: 'two', statusKey: 'draft', tone: 'muted' as const },
  { titleKey: 'three', statusKey: 'pending', tone: 'warning' as const },
];

const PREVIEW_CARD_RADIUS = '12px';

function getDensityScale(density?: DesignSystem['density']) {
  switch (density) {
    case 'compact':
      return { rowPy: 6, rowPx: 12, sectionPy: 10, sectionPx: 12, fontSize: 11, gap: 6 };
    case 'spacious':
      return { rowPy: 14, rowPx: 16, sectionPy: 16, sectionPx: 16, fontSize: 13, gap: 12 };
    default:
      return { rowPy: 10, rowPx: 14, sectionPy: 12, sectionPx: 14, fontSize: 12, gap: 8 };
  }
}

function getCardShadow(designSystem: DesignSystem, isDark: boolean): string {
  const { elevationStyle, shadows } = designSystem;
  const shadowColor = isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(15, 23, 42, 0.14)';
  const softColor = isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(15, 23, 42, 0.08)';
  const smallColor = isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(15, 23, 42, 0.06)';

  // Elevation style is the control surfaced on the design-system page, so it
  // drives the preview. Each style gets a distinct, visible treatment so the
  // choice is always reflected (glass = wide diffuse glow; neumorphism = dual
  // offset light/dark shadows).
  switch (elevationStyle) {
    case 'flat':
      return 'none';
    case 'material':
      return `0 10px 28px ${shadowColor}`;
    case 'soft-shadow':
      return `0 4px 14px ${softColor}`;
    case 'glass':
      return isDark ? '0 8px 32px rgba(0, 0, 0, 0.45)' : '0 8px 32px rgba(15, 23, 42, 0.12)';
    case 'neumorphism':
      return isDark
        ? '6px 6px 14px rgba(0, 0, 0, 0.55), -6px -6px 14px rgba(255, 255, 255, 0.04)'
        : '6px 6px 14px rgba(15, 23, 42, 0.12), -6px -6px 14px rgba(255, 255, 255, 0.9)';
  }

  // No elevation style set — fall back to the legacy `shadows` scale.
  if (shadows === 'none') return 'none';
  if (shadows === 'large') return `0 10px 28px ${shadowColor}`;
  if (shadows === 'medium') return `0 4px 14px ${softColor}`;
  if (shadows === 'small') return `0 2px 8px ${smallColor}`;
  return `0 1px 3px ${isDark ? 'rgba(0, 0, 0, 0.15)' : 'rgba(15, 23, 42, 0.05)'}`;
}

function getInputStyles(
  inputStyle: DesignSystem['inputStyle'],
  border: string,
  surface: string,
  primary: string,
  radius: string,
  isDark: boolean
): React.CSSProperties {
  const textColor = isDark ? '#F3F4F6' : '#111827';
  const base: React.CSSProperties = {
    width: '100%',
    fontSize: 11,
    padding: '6px 10px',
    borderRadius: radius,
    outline: 'none',
    color: textColor,
  };

  switch (inputStyle) {
    case 'filled':
      return { ...base, backgroundColor: `${primary}10`, border: `1px solid transparent` };
    case 'underlined':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${border}`,
        borderRadius: 0,
        paddingLeft: 0,
      };
    case 'floating-label':
      return { ...base, backgroundColor: surface, border: `1px solid ${primary}55` };
    default:
      return { ...base, backgroundColor: surface, border: `1px solid ${border}` };
  }
}

export function DesignSystemLivePreview({
  designSystem,
  logoUrl,
  className,
}: DesignSystemLivePreviewProps): JSX.Element {
  const { t } = useTranslation();
  const isDark = designSystem.theme === 'dark';
  const primary = designSystem.primaryColor ?? '#334155';
  const accent = designSystem.secondaryColor ?? '#64748B';
  const appBg = designSystem.appBg ?? (isDark ? '#111827' : '#F9FAFB');
  const surface = designSystem.surfaceColor ?? (isDark ? '#1F2937' : '#FFFFFF');
  const border = designSystem.borderColor ?? (isDark ? '#374151' : '#E5E7EB');
  const muted = designSystem.mutedTextColor ?? (isDark ? '#9CA3AF' : '#6B7280');
  const success = designSystem.successColor ?? '#22C55E';
  const warning = designSystem.warningColor ?? '#EAB308';
  const error = designSystem.errorColor ?? '#EF4444';
  const info = designSystem.infoColor ?? '#3B82F6';
  const bodyFont = designSystem.fontFamily ?? 'Exo 2';
  const headingFont = designSystem.headingFontFamily ?? bodyFont;
  const listPattern = designSystem.uxPatterns?.listPattern ?? 'table';
  const showSearch = designSystem.uxPatterns?.searchPattern === 'top-bar-inline';
  const activePlatform: Platform = designSystem.activePlatform ?? 'web_app';
  const mobileOs = designSystem.mobileOs ?? DEFAULT_MOBILE_OS;
  const websiteUx = designSystem.websiteUxPatterns ?? {};
  const previewTitle =
    activePlatform === 'mobile'
      ? t('designSystem.preview.titleMobile', { defaultValue: 'Mobile preview' })
      : activePlatform === 'website'
        ? t('designSystem.preview.titleWebsite', { defaultValue: 'Website preview' })
        : t('designSystem.preview.titleWebApp', { defaultValue: 'Web app preview' });

  const density = getDensityScale(designSystem.density);
  const componentRadius = borderRadiusToPx(designSystem.borderRadius);
  const buttonRadius =
    designSystem.borderRadius === 'full' || designSystem.buttonStyle === 'pill'
      ? '9999px'
      : componentRadius;
  const cardShadow = getCardShadow(designSystem, isDark);
  const textPrimary = isDark ? '#F3F4F6' : '#111827';
  const textHeading = isDark ? '#F9FAFB' : '#111827';
  const raisedSurface = isDark ? '#374151' : '#FFFFFF';

  React.useEffect(() => {
    loadGoogleFontPreview(bodyFont);
    if (headingFont !== bodyFont) loadGoogleFontPreview(headingFont);
  }, [bodyFont, headingFont]);

  const renderListContent = () => {
    if (listPattern === 'cards') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: density.gap }}>
          {SAMPLE_LIST_ITEMS.map((item) => (
            <div
              key={item.titleKey}
              style={{
                padding: `${density.rowPy}px ${density.rowPx}px`,
                borderRadius: componentRadius,
                border: `1px solid ${border}`,
                backgroundColor: raisedSurface,
              }}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontSize: density.fontSize, fontWeight: 500, color: textPrimary }}>
                  {t(`designSystem.preview.item.${item.titleKey}`)}
                </span>
                <StatusBadge
                  item={item}
                  t={t}
                  primary={primary}
                  muted={muted}
                  success={success}
                  warning={warning}
                  radius={buttonRadius}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (listPattern === 'table') {
      return (
        <div
          style={{
            border: `1px solid ${border}`,
            borderRadius: componentRadius,
            overflow: 'hidden',
          }}
        >
          <div
            className="grid grid-cols-[1fr_auto]"
            style={{
              padding: `${density.rowPy - 2}px ${density.rowPx}px`,
              backgroundColor: `${muted}12`,
              borderBottom: `1px solid ${border}`,
              fontSize: 10,
              fontWeight: 600,
              color: muted,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <span>{t('designSystem.preview.name')}</span>
            <span>{t('designSystem.preview.status')}</span>
          </div>
          {SAMPLE_LIST_ITEMS.map((item, idx) => (
            <div
              key={item.titleKey}
              className="grid grid-cols-[1fr_auto] items-center"
              style={{
                padding: `${density.rowPy}px ${density.rowPx}px`,
                borderBottom:
                  idx < SAMPLE_LIST_ITEMS.length - 1 ? `1px solid ${border}` : undefined,
                fontSize: density.fontSize,
              }}
            >
              <span style={{ fontWeight: 500, color: textPrimary }}>
                {t(`designSystem.preview.item.${item.titleKey}`)}
              </span>
              <StatusBadge
                item={item}
                t={t}
                primary={primary}
                muted={muted}
                success={success}
                warning={warning}
                radius={buttonRadius}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        {SAMPLE_LIST_ITEMS.map((item, idx) => (
          <div
            key={item.titleKey}
            className="flex items-center justify-between"
            style={{
              padding: `${density.rowPy}px 0`,
              borderBottom: idx < SAMPLE_LIST_ITEMS.length - 1 ? `1px solid ${border}` : undefined,
            }}
          >
            <div>
              <p style={{ fontSize: density.fontSize, fontWeight: 500, color: textPrimary }}>
                {t(`designSystem.preview.item.${item.titleKey}`)}
              </p>
              <p style={{ fontSize: density.fontSize - 1, color: muted }}>
                {t('designSystem.preview.updatedAgo')}
              </p>
            </div>
            <StatusBadge
              item={item}
              t={t}
              primary={primary}
              muted={muted}
              success={success}
              warning={warning}
              radius={buttonRadius}
            />
          </div>
        ))}
      </div>
    );
  };

  // Mobile preview — a phone frame reflecting the selected OS conventions
  // (iOS inset grouped list + pull-down search + bottom tab bar; Android card
  // list + FAB + nav bar). Brand/colors/type are shared with the other platforms.
  const renderMobile = () => {
    const isIos = mobileOs === 'ios';
    const convValue = (key: string) =>
      MOBILE_CONVENTIONS[mobileOs].find((r) => r.rowKey === key)?.value ?? '';
    const subtitle = `${isIos ? 'iOS' : 'Android'} · ${convValue('listDisplay')} · ${convValue('search')}`;
    const bezel = isDark ? '#0b0f17' : '#111827';
    // Android grouped-list surface tint (Material tonal container).
    const tonal = isDark ? `${primary}26` : `${primary}0F`;
    const rowAvatar = (
      <span
        className="shrink-0 rounded-full"
        style={{ width: 16, height: 16, backgroundColor: `${primary}29` }}
      />
    );
    const rowLabel = (titleKey: string) => (
      <span
        className="flex-1 truncate"
        style={{ fontWeight: 500, color: textPrimary, fontSize: density.fontSize }}
      >
        {t(`designSystem.preview.item.${titleKey}`)}
      </span>
    );
    const chevron = <ChevronRight className="size-3.5 shrink-0" style={{ color: muted }} />;
    const tabs = ['Home', 'Scopes', 'Profile'];
    return (
      <div
        className="flex justify-center rounded-xl bg-gray-100 p-4 dark:bg-gray-900/40"
        style={{ fontFamily: bodyFont }}
      >
        <div
          className="relative flex w-[220px] flex-col overflow-hidden"
          style={{
            height: 468,
            borderRadius: 28,
            border: `6px solid ${isDark ? '#111827' : '#1f2937'}`,
            backgroundColor: appBg,
            boxShadow: cardShadow,
          }}
        >
          {/* Status bar + notch: iOS pill (Dynamic Island) vs Android punch-hole. */}
          <div
            className="flex shrink-0 items-center justify-center"
            style={{ height: isIos ? 22 : 18, backgroundColor: isIos ? surface : appBg }}
          >
            <div
              className="rounded-full"
              style={{
                width: isIos ? 44 : 9,
                height: isIos ? 12 : 9,
                backgroundColor: bezel,
              }}
            />
          </div>

          {isIos ? (
            /* iOS: centered nav bar — circular menu / title / circular search. */
            <div
              className="flex shrink-0 items-center justify-between border-b"
              style={{ borderColor: border, backgroundColor: surface, padding: `6px ${density.sectionPx}px` }}
            >
              <span
                className="flex size-6 items-center justify-center rounded-full"
                style={{ backgroundColor: `${primary}14`, color: primary }}
              >
                <Menu className="size-3.5" />
              </span>
              {logoUrl ? (
                <img src={logoUrl} alt="Brand logo" className="h-5 max-w-[70px] object-contain" />
              ) : (
                <span className="text-sm font-semibold" style={{ color: primary, fontFamily: headingFont }}>
                  brand.
                </span>
              )}
              <span
                className="flex size-6 items-center justify-center rounded-full"
                style={{ backgroundColor: `${primary}14`, color: primary }}
              >
                <Search className="size-3.5" />
              </span>
            </div>
          ) : (
            /* Android: top app bar — plain menu + search icons, left-aligned. */
            <div
              className="flex shrink-0 items-center justify-between"
              style={{ backgroundColor: surface, padding: `10px ${density.sectionPx}px` }}
            >
              <Menu className="size-4" style={{ color: textHeading }} />
              <Search className="size-4" style={{ color: textHeading }} />
            </div>
          )}

          <div
            className="flex-1 overflow-auto"
            style={{ padding: `${density.sectionPy}px ${density.sectionPx}px` }}
          >
            {!isIos && (
              /* Android large left-aligned title + colored section label. */
              <>
                {logoUrl ? (
                  <img src={logoUrl} alt="Brand logo" className="h-6 max-w-[90px] object-contain" />
                ) : (
                  <p
                    className="font-semibold"
                    style={{ fontSize: density.fontSize + 7, color: primary, fontFamily: headingFont }}
                  >
                    brand.
                  </p>
                )}
                <p
                  className="mt-2 text-[9px] font-semibold uppercase"
                  style={{ color: primary, letterSpacing: '0.04em' }}
                >
                  {t('designSystem.preview.components', { defaultValue: 'Components' })}
                </p>
              </>
            )}
            <p className="mb-2 mt-2 text-[10px]" style={{ color: muted }}>
              {subtitle}
            </p>

            {isIos ? (
              /* iOS inset grouped list: white card, dividers indented after the icon. */
              <div
                style={{
                  border: `1px solid ${border}`,
                  borderRadius: componentRadius,
                  overflow: 'hidden',
                  backgroundColor: raisedSurface,
                }}
              >
                {SAMPLE_LIST_ITEMS.map((item, idx) => (
                  <React.Fragment key={item.titleKey}>
                    <div
                      className="flex items-center gap-2"
                      style={{ padding: `${density.rowPy}px ${density.rowPx}px` }}
                    >
                      {rowAvatar}
                      {rowLabel(item.titleKey)}
                      {chevron}
                    </div>
                    {idx < SAMPLE_LIST_ITEMS.length - 1 && (
                      <div
                        style={{
                          height: 1,
                          marginLeft: density.rowPx + 16 + 8,
                          backgroundColor: border,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              /* Android grouped tonal card: tinted container, full-width rows. */
              <div
                style={{
                  borderRadius: componentRadius,
                  overflow: 'hidden',
                  backgroundColor: tonal,
                }}
              >
                {SAMPLE_LIST_ITEMS.map((item) => (
                  <div
                    key={item.titleKey}
                    className="flex items-center gap-2"
                    style={{ padding: `${density.rowPy}px ${density.rowPx}px` }}
                  >
                    {rowAvatar}
                    {rowLabel(item.titleKey)}
                    {chevron}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Android FAB sits above the navigation bar. */}
          {!isIos && (
            <div className="absolute" style={{ right: 12, bottom: 60 }}>
              <div
                className="flex size-10 items-center justify-center text-lg text-white"
                style={{ backgroundColor: primary, borderRadius: 18, boxShadow: cardShadow }}
              >
                +
              </div>
            </div>
          )}

          {/* Bottom bar + home indicator. iOS tab bar (dot + label) vs Android
              navigation bar (active item has a Material pill highlight). */}
          <div
            className="flex shrink-0 items-start justify-around border-t"
            style={{ borderColor: border, padding: '6px 0 2px', backgroundColor: surface }}
          >
            {tabs.map((tab, i) => {
              const active = i === 0;
              return (
                <span key={tab} className="flex flex-col items-center gap-0.5">
                  {isIos ? (
                    <span
                      className="rounded-full"
                      style={{ width: 6, height: 6, backgroundColor: active ? primary : muted }}
                    />
                  ) : (
                    <span
                      className="flex items-center justify-center rounded-full"
                      style={{
                        width: 22,
                        height: 12,
                        backgroundColor: active ? `${primary}26` : 'transparent',
                      }}
                    >
                      <span
                        className="rounded-full"
                        style={{ width: 6, height: 6, backgroundColor: active ? primary : muted }}
                      />
                    </span>
                  )}
                  <span className="text-[9px]" style={{ color: active ? primary : muted }}>
                    {tab}
                  </span>
                </span>
              );
            })}
          </div>
          <div className="flex shrink-0 justify-center" style={{ padding: '3px 0 5px' }}>
            <div
              className="rounded-full"
              style={{ width: isIos ? 64 : 80, height: 3, backgroundColor: textPrimary, opacity: 0.35 }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Website preview — a marketing layout reflecting the website UX patterns
  // (nav style, hero style, list display). Brand/colors/type stay shared.
  const renderWebsite = () => {
    const nav = websiteUx.navPattern ?? 'top-bar';
    const hero = websiteUx.heroStyle ?? 'centered';
    const list = websiteUx.listDisplay ?? 'card-grid';
    const ctaLabel = t('designSystem.preview.website.cta', { defaultValue: 'Get started' });
    const heroTitle = t('designSystem.preview.website.heroTitle', {
      defaultValue: 'Build it once. Ship everywhere.',
    });
    const heroSub = t('designSystem.preview.website.heroSub', {
      defaultValue: 'One clear story for every visitor.',
    });
    return (
      <div className="rounded-xl p-4" style={{ backgroundColor: appBg, fontFamily: bodyFont }}>
        <div
          className="overflow-hidden border"
          style={{
            borderRadius: PREVIEW_CARD_RADIUS,
            borderColor: border,
            backgroundColor: surface,
            boxShadow: cardShadow,
          }}
        >
          <div
            className="flex items-center justify-between gap-2 border-b"
            style={{ borderColor: border, padding: `${density.sectionPy}px ${density.sectionPx}px` }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Brand logo" className="h-6 max-w-[80px] object-contain" />
            ) : (
              <span
                className="text-sm font-semibold italic"
                style={{ color: primary, fontFamily: headingFont }}
              >
                brand.
              </span>
            )}
            {nav !== 'minimal-header' && (
              <div className="flex items-center gap-3 text-[10px]" style={{ color: muted }}>
                <span>Product</span>
                <span>Pricing</span>
                <span>About</span>
              </div>
            )}
            <button
              type="button"
              className="shrink-0 px-2.5 py-1 text-[10px] font-medium text-white"
              style={{ backgroundColor: primary, borderRadius: buttonRadius }}
            >
              {ctaLabel}
            </button>
          </div>
          <div style={{ padding: `${density.sectionPy + 6}px ${density.sectionPx}px` }}>
            {hero === 'split-visual' ? (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p
                    className="font-semibold"
                    style={{
                      fontSize: density.fontSize + 6,
                      color: textHeading,
                      fontFamily: headingFont,
                      lineHeight: 1.2,
                    }}
                  >
                    {heroTitle}
                  </p>
                  <p className="mt-1 text-[10px]" style={{ color: muted }}>
                    {heroSub}
                  </p>
                  <button
                    type="button"
                    className="mt-2 px-2.5 py-1 text-[10px] font-medium text-white"
                    style={{ backgroundColor: primary, borderRadius: buttonRadius }}
                  >
                    {ctaLabel}
                  </button>
                </div>
                <div
                  className="h-16 w-20 shrink-0"
                  style={{ backgroundColor: `${primary}18`, borderRadius: componentRadius }}
                />
              </div>
            ) : (
              <div
                className="text-center"
                style={
                  hero === 'video-bg'
                    ? { backgroundColor: `${primary}14`, borderRadius: componentRadius, padding: 12 }
                    : undefined
                }
              >
                <p
                  className="font-semibold"
                  style={{
                    fontSize: density.fontSize + 6,
                    color: textHeading,
                    fontFamily: headingFont,
                    lineHeight: 1.2,
                  }}
                >
                  {heroTitle}
                </p>
                <p className="mx-auto mt-1 max-w-[80%] text-[10px]" style={{ color: muted }}>
                  {heroSub}
                </p>
                <button
                  type="button"
                  className="mt-2 px-2.5 py-1 text-[10px] font-medium text-white"
                  style={{ backgroundColor: primary, borderRadius: buttonRadius }}
                >
                  {ctaLabel}
                </button>
              </div>
            )}
          </div>
          <div
            className="border-t"
            style={{ borderColor: border, padding: `${density.sectionPy}px ${density.sectionPx}px` }}
          >
            {list === 'card-grid' ? (
              <div className="grid grid-cols-3 gap-2">
                {SAMPLE_LIST_ITEMS.map((item) => (
                  <div
                    key={item.titleKey}
                    style={{
                      border: `1px solid ${border}`,
                      borderRadius: componentRadius,
                      padding: 8,
                      backgroundColor: raisedSurface,
                    }}
                  >
                    <div
                      className="mb-1 h-6 w-full"
                      style={{ backgroundColor: `${primary}14`, borderRadius: 4 }}
                    />
                    <p style={{ fontSize: density.fontSize - 1, fontWeight: 500, color: textPrimary }}>
                      {t(`designSystem.preview.item.${item.titleKey}`)}
                    </p>
                  </div>
                ))}
              </div>
            ) : list === 'feature-rows' ? (
              <div className="flex flex-col" style={{ gap: density.gap }}>
                {SAMPLE_LIST_ITEMS.map((item) => (
                  <div
                    key={item.titleKey}
                    className="flex items-center gap-2"
                    style={{
                      border: `1px solid ${border}`,
                      borderRadius: componentRadius,
                      padding: 8,
                      backgroundColor: raisedSurface,
                    }}
                  >
                    <div
                      className="size-7 shrink-0"
                      style={{ backgroundColor: `${primary}14`, borderRadius: 6 }}
                    />
                    <p style={{ fontSize: density.fontSize, fontWeight: 500, color: textPrimary }}>
                      {t(`designSystem.preview.item.${item.titleKey}`)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {SAMPLE_LIST_ITEMS.map((item, idx) => (
                  <div
                    key={item.titleKey}
                    className="flex items-center justify-between"
                    style={{
                      padding: `${density.rowPy}px 0`,
                      borderBottom:
                        idx < SAMPLE_LIST_ITEMS.length - 1 ? `1px solid ${border}` : undefined,
                      fontSize: density.fontSize,
                    }}
                  >
                    <span style={{ fontWeight: 500, color: textPrimary }}>
                      {t(`designSystem.preview.item.${item.titleKey}`)}
                    </span>
                    <StatusBadge
                      item={item}
                      t={t}
                      primary={primary}
                      muted={muted}
                      success={success}
                      warning={warning}
                      radius={buttonRadius}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <aside className={cn('flex h-full flex-col bg-white dark:bg-gray-900', className)}>
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{previewTitle}</span>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50 p-4 dark:bg-gray-950/50">
        <p className="mb-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          {t('designSystem.preview.sampleDesc')}
        </p>

        {activePlatform === 'web_app' && (
        <div className="rounded-xl p-4" style={{ backgroundColor: appBg, fontFamily: bodyFont }}>
          <div
            className="overflow-hidden border"
            style={{
              borderRadius: PREVIEW_CARD_RADIUS,
              borderColor: border,
              backgroundColor: surface,
              boxShadow: cardShadow,
            }}
          >
            {/* App bar */}
            <div
              className="border-b"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                {logoUrl ? (
                  <img src={logoUrl} alt="Brand logo" className="h-6 max-w-[80px] object-contain" />
                ) : (
                  <span
                    className="text-sm font-semibold italic"
                    style={{ color: primary, fontFamily: headingFont }}
                  >
                    brand.
                  </span>
                )}
                <button
                  type="button"
                  className="shrink-0 px-2.5 py-1 text-[11px] font-medium text-white"
                  style={{ backgroundColor: primary, borderRadius: buttonRadius }}
                >
                  {t('designSystem.preview.newItem')}
                </button>
              </div>
              {showSearch && (
                <input
                  readOnly
                  placeholder={t('designSystem.preview.searchPlaceholder')}
                  className={cn(
                    'mt-2',
                    isDark ? 'placeholder:text-gray-500' : 'placeholder:text-gray-400'
                  )}
                  style={getInputStyles(
                    designSystem.inputStyle,
                    border,
                    surface,
                    primary,
                    componentRadius,
                    isDark
                  )}
                />
              )}
            </div>

            {/* List section */}
            <div style={{ padding: `${density.sectionPy}px ${density.sectionPx}px` }}>
              <p
                className="mb-0.5 font-semibold"
                style={{
                  fontSize: density.fontSize + 1,
                  color: textHeading,
                  fontFamily: headingFont,
                }}
              >
                {t('designSystem.preview.sampleListView')}
              </p>
              <p className="mb-2 text-[10px]" style={{ color: muted }}>
                {t('designSystem.preview.layoutDensity', {
                  layout: t(
                    `designSystem.preview.${
                      listPattern === 'table'
                        ? 'layoutTable'
                        : listPattern === 'cards'
                          ? 'layoutCards'
                          : 'layoutRows'
                    }`
                  ),
                  density: t(`designSystem.preview.density.${designSystem.density ?? 'comfortable'}`, {
                    defaultValue: designSystem.density ?? 'comfortable',
                  }),
                })}
              </p>
              {renderListContent()}
            </div>

            {/* Input sample */}
            {!showSearch && (
              <div
                className="border-t"
                style={{
                  borderColor: border,
                  padding: `${density.sectionPy}px ${density.sectionPx}px`,
                }}
              >
                <p className="mb-1.5 text-[10px] font-medium" style={{ color: muted }}>
                  {t('designSystem.preview.inputCaption', {
                    style: t(`designSystem.preview.inputStyle.${designSystem.inputStyle ?? 'outlined'}`, {
                      defaultValue: designSystem.inputStyle ?? 'outlined',
                    }),
                  })}
                </p>
                <input
                  readOnly
                  placeholder={t('designSystem.preview.sampleField')}
                  className={isDark ? 'placeholder:text-gray-500' : 'placeholder:text-gray-400'}
                  style={getInputStyles(
                    designSystem.inputStyle,
                    border,
                    surface,
                    primary,
                    componentRadius,
                    isDark
                  )}
                />
              </div>
            )}

            {/* Buttons */}
            <div
              className="flex flex-wrap items-center gap-2 border-t"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              <button
                type="button"
                className="px-2.5 py-1 text-[11px] font-medium text-white"
                style={{ backgroundColor: primary, borderRadius: buttonRadius }}
              >
                {t('designSystem.preview.btnPrimary')}
              </button>
              <button
                type="button"
                className="border px-2.5 py-1 text-[11px] font-medium"
                style={{
                  borderColor: accent,
                  color: primary,
                  backgroundColor: 'transparent',
                  borderRadius: buttonRadius,
                }}
              >
                {t('designSystem.preview.btnSecondary')}
              </button>
              <button
                type="button"
                className="px-1 py-1 text-[11px] font-medium underline-offset-2"
                style={{ color: primary }}
              >
                {t('designSystem.preview.btnLink')}
              </button>
            </div>

            {/* Semantic colors */}
            <div
              className="flex flex-wrap gap-1.5 border-t"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              {(
                [
                  { key: 'success', color: success },
                  { key: 'warning', color: warning },
                  { key: 'error', color: error },
                  { key: 'info', color: info },
                ] as const
              ).map(({ key, color }) => (
                <span
                  key={key}
                  className="px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    borderRadius: buttonRadius,
                    backgroundColor: `${color}18`,
                    color,
                  }}
                >
                  {t(`designSystem.preview.semantic.${key}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
        )}

        {activePlatform === 'mobile' && renderMobile()}
        {activePlatform === 'website' && renderWebsite()}
      </div>

      <div className="shrink-0 border-t border-gray-100 px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
        <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
          {t('designSystem.preview.appliesTo')}
        </p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          {t('designSystem.preview.appliesToDesc')}
        </p>
      </div>
    </aside>
  );
}

function StatusBadge({
  item,
  primary,
  muted,
  success,
  warning,
  radius,
  t,
}: {
  item: (typeof SAMPLE_LIST_ITEMS)[number];
  primary: string;
  muted: string;
  success: string;
  warning: string;
  radius: string;
  t: (key: string) => string;
}): JSX.Element {
  const color =
    item.tone === 'success'
      ? success
      : item.tone === 'warning'
        ? warning
        : item.tone === 'muted'
          ? muted
          : primary;

  return (
    <span
      className="px-2 py-0.5 text-[10px] font-medium"
      style={{
        borderRadius: radius,
        backgroundColor: `${color}18`,
        color,
      }}
    >
      {t(`designSystem.preview.itemStatus.${item.statusKey}`)}
    </span>
  );
}

export default DesignSystemLivePreview;
